import Layout from "@/components/layout/Layout";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Send, CheckCircle } from "lucide-react";

const WEB3FORMS_ACCESS_KEY = "9d14a5f1-d64f-4240-b020-9d0b7a953355";

export default function Contact() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    // Append Web3Forms access key and metadata
    formData.append("access_key", WEB3FORMS_ACCESS_KEY);
    formData.append("subject", "New Contact Form Submission — Pronto-Letters.com");
    formData.append("from_name", "Pronto-Letters.com");
    // Honeypot spam protection
    formData.append("botcheck", "");

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        setSubmitted(true);
        form.reset();
        toast({
          title: "Message sent!",
          description: "We'll get back to you within 1 business day.",
        });
      } else {
        throw new Error(data.message || "Submission failed");
      }
    } catch (error) {
      toast({
        title: "Something went wrong",
        description: "Please try again or email us directly at nick@moonriversigns.com",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <SEO
        title="Contact Us"
        description="Contact Pronto Letters for orders, custom quotes, or questions about Gemini Pronto™ changeable letter sets and accessories. Call Nick at 314-369-8649."
        canonical="/contact"
      />
      <div className="container max-w-lg py-16">
        <h1 className="font-display text-4xl font-bold">Contact Us</h1>
        <p className="mt-2 text-muted-foreground">
          Have a question? Send us a message and we'll get back to you within 1 business day.
        </p>

        {submitted ? (
          <div className="mt-12 flex flex-col items-center gap-4 rounded-lg border border-green-200 bg-green-50 p-8 text-center">
            <CheckCircle className="h-12 w-12 text-green-500" />
            <h2 className="text-xl font-semibold text-green-800">Message Received!</h2>
            <p className="text-green-700">
              Thanks for reaching out. We'll get back to you within 1 business day.
            </p>
            <Button
              variant="outline"
              className="mt-2"
              onClick={() => setSubmitted(false)}
            >
              Send Another Message
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            {/* Honeypot field — hidden from real users, catches bots */}
            <input type="checkbox" name="botcheck" className="hidden" style={{ display: "none" }} />

            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" name="name" required placeholder="Your name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" name="email" type="email" required placeholder="you@example.com" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone (optional)</Label>
              <Input id="phone" name="phone" type="tel" placeholder="(314) 555-0000" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                name="message"
                required
                placeholder="How can we help? Ask about products, bulk orders, or custom quotes."
                rows={5}
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-accent text-accent-foreground hover:bg-accent/90"
              disabled={loading}
            >
              <Send className="mr-2 h-4 w-4" />
              {loading ? "Sending..." : "Send Message"}
            </Button>
          </form>
        )}

        <div className="mt-10 border-t pt-8 text-sm text-muted-foreground">
          <p className="font-medium text-foreground">Prefer to reach us directly?</p>
          <p className="mt-1">
            Email:{" "}
            <a href="mailto:nick@moonriversigns.com" className="text-accent hover:underline">
              nick@moonriversigns.com
            </a>
          </p>
          <p className="mt-1">
            Phone:{" "}
            <a href="tel:3143698649" className="text-accent hover:underline">
              (314) 369-8649
            </a>
          </p>
        </div>
      </div>
    </Layout>
  );
}
